import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// sortable
// ---------------------------------------------------------------
// Long-press drag to reorder a list, persisted on drop.
//
//   <ul data-controller="sortable"
//       data-sortable-url-value="/en/budgets/reorder">
//     <li data-id="abc123">…</li>
//   </ul>
//
// Each item carries the id to submit in data-id. On drop the whole order is
// PATCHed as ids[], so the server never has to reason about deltas — it just
// writes positions in the order it receives.
//
// Omit the url and nothing is persisted: the drop only fires a sortable:end
// event (detail: oldIndex, newIndex) for a host controller that owns the order
// itself, e.g. a form that submits it with everything else.
export default class extends Controller {
  static values = {
    url: String,
    // Hold this long before the item lifts. Below ~200ms a scroll flick starts
    // dragging rows instead of scrolling the page.
    delay: { type: Number, default: 300 }
  }

  connect() {
    this.sortable = Sortable.create(this.element, {
      animation: 180,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      delay: this.delayValue,
      // Touch only: a mouse drag should start immediately, and desktop has no
      // scroll gesture to disambiguate from.
      delayOnTouchOnly: true,
      // Movement over this many px during the delay cancels the lift, so a
      // vertical scroll that starts on a row still scrolls.
      touchStartThreshold: 4,
      // A press on a control inside a row is a tap, never the start of a drag.
      // preventOnFilter stays off so the control's own click still fires.
      filter: "button, a, input, select, textarea",
      preventOnFilter: false,
      forceFallback: true,
      fallbackTolerance: 3,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      // Fires the moment the row lifts, which is where the haptic belongs —
      // it confirms the long press landed before the finger has moved.
      onChoose: () => this.dispatch("lift"),
      onEnd: (event) => {
        if (event.oldIndex === event.newIndex) return
        this.dispatch("end", { detail: { oldIndex: event.oldIndex, newIndex: event.newIndex } })
        if (this.urlValue) this._persist()
      }
    })
  }

  disconnect() {
    this.sortable?.destroy()
    this.sortable = null
  }

  // For lists where dragging shares its pixels with another gesture: the host
  // controller turns dragging off while that gesture owns them.
  setDisabled(disabled) {
    this.sortable?.option("disabled", disabled)
  }

  async _persist() {
    const ids = Array.from(this.element.children)
      .map((item) => item.dataset.id)
      .filter(Boolean)

    const body = new FormData()
    ids.forEach((id) => body.append("ids[]", id))

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          "Accept": "text/vnd.turbo-stream.html, text/html"
        }
      })
      if (!response.ok) throw new Error(response.statusText)
      // Bubbles to document: the form that owns Save lives outside this list.
      this.dispatch("reordered")
    } catch (error) {
      // The order on screen no longer matches the server. Reloading is the
      // honest recovery — a silently wrong order would follow the user around
      // every screen that lists budgets.
      console.error("[sortable] reorder failed", error)
      window.location.reload()
    }
  }
}
